import { useMutation, useQuery } from '@apollo/client';
import { Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import {
  createWorkOrderComment as createWorkOrderCommentMutation,
  deleteWorkOrderComment as deleteWorkOrderCommentMutation,
  updateWorkOrderComment as updateWorkOrderCommentMutation,
  getWorkOrderCommentsByWorkOrderId,
  WorkOrderComment as WorkOrderCommentType,
  WorkOrder,
  PermissionsEnum,
  MIXPANEL_EVENTS,
} from 'vendor-marketplace-shared-library';

import PrimaryButton from '../../../components/common/styled-mui-overrides/PrimaryButton';
import WorkOrderComment from '../../../components/work-orders/WorkOrderComment';
import { DELETE_WORK_ORDER_COMMENT, UPDATE_WORK_ORDER_COMMENT } from '../../../error-messages';
import useAlert from '../../../hooks/useAlert';
import useAuth from '../../../hooks/useAuth';
import useMixpanel from '../../../hooks/useMixpanel';

type WorkOrderCommentsModuleProps = {
  workOrder: WorkOrder;
};

export default function WorkOrderCommentsModule(props: WorkOrderCommentsModuleProps) {
  const alert = useAlert();
  const { validateWorkOrderUserPermissions } = useAuth();
  const { mixpanelTrack } = useMixpanel();
  const [commentContent, setCommentContent] = useState('');
  const { data, loading } = useQuery(getWorkOrderCommentsByWorkOrderId, {
    variables: {
      workOrderId: props.workOrder?.id,
    },
    skip: !props.workOrder?.id,
  });

  const [createWorkOrderComment] = useMutation(createWorkOrderCommentMutation, {
    variables: {
      input: {
        workOrderId: Number(props.workOrder.id),
        content: commentContent,
      },
    },
    onCompleted: () => {
      setCommentContent('');
    },
    refetchQueries: [getWorkOrderCommentsByWorkOrderId],
  });

  const [updateWorkOrderComment] = useMutation(updateWorkOrderCommentMutation, {
    refetchQueries: [getWorkOrderCommentsByWorkOrderId],
    onError: () => {
      alert.setAlert({ message: UPDATE_WORK_ORDER_COMMENT });
    },
  });

  const onEdit = async (commentId: string, content: string) => {
    mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_DETAILS_EDIT_COMMENT_PRESSED);
    await updateWorkOrderComment({
      variables: {
        id: commentId,
        input: {
          content,
        },
      },
    });
  };

  const [deleteWorkOrderComment] = useMutation(deleteWorkOrderCommentMutation, {
    refetchQueries: [getWorkOrderCommentsByWorkOrderId],
    onError: () => {
      alert.setAlert({ message: DELETE_WORK_ORDER_COMMENT });
    },
  });

  const onDelete = async (id: string) => {
    await deleteWorkOrderComment({ variables: { id: Number(id) } });
  };

  const onSubmit = async () => {
    if (!commentContent) return;
    await createWorkOrderComment();
  };

  if (loading && !data) return <div>Loading...</div>;

  return (
    <Grid container rowGap={2}>
      <Typography variant={'h6'}>Comments</Typography>
      <Grid
        container
        direction={'column'}
        rowGap={2}
        p={2}
        borderRadius={2}
        boxShadow={2}
        sx={{
          backgroundColor: 'secondary.light',
        }}
      >
        {data?.getWorkOrderCommentsByWorkOrderId?.length > 0 ? (
          data?.getWorkOrderCommentsByWorkOrderId.map((comment: WorkOrderCommentType) => (
            <WorkOrderComment
              key={comment.id}
              workOrderComment={comment}
              onDelete={onDelete}
              onEdit={onEdit}
              disableMenu={
                !validateWorkOrderUserPermissions(
                  props.workOrder,
                  PermissionsEnum.WORK_ORDER_DETAILS_UPDATE,
                )
              }
            />
          ))
        ) : (
          <Typography>No comments yet</Typography>
        )}

        {/* New comment input */}
        <Grid item>
          <Grid container columnGap={2} direction={'row'} alignItems={'center'} wrap={'nowrap'}>
            <TextField
              multiline
              fullWidth
              value={commentContent}
              onChange={e => setCommentContent(e.target.value)}
              placeholder={'New comment'}
              sx={{ backgroundColor: 'common.white' }}
            />
            <PrimaryButton
              onClick={() => {
                mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_DETAILS_CREATE_COMMENT_PRESSED);
                onSubmit();
              }}
            >
              Submit
            </PrimaryButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
